import { HeaderCheckoutElementDataType } from '@/components/sections/builder/elements/header/header-checkout/type';
import CartButton from '@/components/sections/builder/elements/header/header/cart-button';
import HeaderLogo from '@/components/sections/builder/elements/header/header/header-logo';
import { ElementProps } from '@/components/sections/builder/types';
import useScrollDirection from '@/hooks/useScrollDirection';
import { cn } from '@/lib/utils';
import { ThemeType } from '@/stores/builder';
import { XIcon } from 'lucide-react';
import React from 'react';

export default function ElementContentHeaderCheckout({ element, preview, theme }: ElementProps) {
  const scrollDirection = useScrollDirection();

  const [headerFixed, setHeaderFixed] = React.useState<boolean>(false);
  const [headerStatic, setHeaderStatic] = React.useState<boolean>(true);
  const [collapsed, setCollapsed] = React.useState<boolean>(true);

  const data = element.data as HeaderCheckoutElementDataType;

  const header = typeof document !== 'undefined' ? document.getElementById('header-sticky') : null;
  const wrapperHeader =
    typeof document !== 'undefined'
      ? document.getElementById('wrapper-header-checkout')?.offsetHeight || 0
      : 0;

  const headerHeight = header?.offsetHeight || 0;
  const sticky = Number(header?.offsetTop);

  const handleScroll = () => {
    const scrollOffset = window.scrollY;
    const spacing = data.spacing || 0;

    if (['on-scroll-up', 'always'].includes(data.stickyHeader as string)) {
      setHeaderFixed(
        data.stickyHeader === 'on-scroll-up' && scrollOffset > sticky + wrapperHeader - spacing
      );
      setHeaderStatic(scrollOffset <= sticky + (wrapperHeader - headerHeight) - spacing);
    }
  };

  if (typeof window !== 'undefined') {
    if (['on-scroll-up', 'always'].includes(data.stickyHeader as string)) {
      window.addEventListener('scroll', handleScroll);
    } else {
      window.removeEventListener('scroll', handleScroll);
    }
  }

  return (
    <>
      <div
        className={cn(
          'font-scale fixed inset-0 z-50 block transform overflow-hidden bg-gray-900 bg-opacity-25 ease-in-out md:hidden',
          !collapsed
            ? '-translate-x-0 opacity-100 transition-opacity'
            : '-translate-x-full opacity-0 transition-all'
        )}
      >
        <div
          className={cn(
            'absolute left-0 h-full w-[90%] transform bg-white text-left shadow-xl transition-all delay-200 duration-200 ease-in-out',
            !collapsed ? '-translate-x-0' : '-translate-x-full'
          )}
        >
          <div className='relative flex h-full w-full flex-col space-y-1 overflow-y-auto p-6'>
            <div className='mb-4 flex justify-between'>
              <HeaderLogo {...data} />
              <button type='button' onClick={() => setCollapsed(true)}>
                <XIcon className='h-6 w-6' />
              </button>
            </div>
          </div>
        </div>
        <div className='h-full w-full cursor-pointer' onClick={() => setCollapsed(true)}></div>
      </div>
      <header
        id='header-sticky'
        className={cn(
          'padding inset-x-0 z-40 bg-white px-6 py-3 text-sm md:p-6',
          headerFixed && 'offset-top fixed duration-300',
          (scrollDirection === 'up' || data.stickyHeader === 'always') &&
            !headerStatic &&
            'fixed !top-0 duration-300',
          ['top-center'].includes(data.logoPosition as string)
            ? 'justify-center md:justify-between'
            : 'justify-start md:justify-between',
          ['top-left', 'top-center'].includes(data.logoPosition as string)
            ? 'items-center md:items-start md:justify-between'
            : 'items-center',
          data.isShowSeparator && 'border-b',
          'font-scale header-full-width'
        )}
      >
        <div className='theme_layout_page_width mx-auto flex w-full items-center justify-between'>
          <div
            className={cn(
              'hidden items-center md:flex md:gap-x-12',
              data.logoPosition === 'left' && 'flex-col items-start gap-y-6 md:w-full',
              data.logoPosition === 'center' && 'flex-col gap-y-6 md:w-full'
            )}
          >
            <div className={cn(data.logoPosition !== 'middle-center' ? 'flex' : 'hidden')}>
              <HeaderLogo {...data} />
            </div>
          </div>
          <div
            className={cn(
              'flex w-full py-2 md:hidden',
              data.mobileLogoPosition === 'left' && 'justify-start',
              data.mobileLogoPosition === 'center' && 'justify-center'
            )}
          >
            <HeaderLogo {...data} />
          </div>
          {data.isShowCart && (
            <div className='hidden min-w-[140px] items-center justify-end gap-x-4 md:flex'>
              <CartButton
                host={typeof window !== 'undefined' ? window.location.host : ''}
                preview={!!preview}
                theme={theme as ThemeType}
              />
            </div>
          )}
        </div>
      </header>
      <div
        className={cn(
          (headerFixed ||
            ((scrollDirection === 'up' || data.stickyHeader === 'always') && !headerStatic)) &&
            'header-height'
        )}
      ></div>
      <style jsx>
        {`
          @media (min-width: 768px) {
            .padding {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${data.padding?.left || 0}px;
              padding-right: ${data.padding?.right || 0}px;
            }
          }
          .offset-top {
            top: -${headerHeight}px;
          }
          .header-height {
            padding-top: ${headerHeight}px;
          }
          .header-spacing {
            height: ${data.spacing}px;
          }
          .header-full-width {
            max-width: unset;
            width: 100%;
          }
        `}
      </style>
    </>
  );
}
